import { useTranslation } from 'react-i18next';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext';
import Filter, { defaultFormatLabel } from '../../../../core/components/Filter.tsx';
import * as React from 'react';
import { camelCase } from 'lodash';
import DropdownMenu from '../../../../core/components/DropdownMenu';
import SwitchRow from '../../../../core/components/SwitchRow.tsx';
import { useQuery } from '@apollo/client';
import { GET_SMART_INBOX_INTEGRATIONS } from '../../../../customer/queries.ts';
import { IFilter } from '../../../../core/utils/filterQuery.ts';
import { UserFilterName } from '../../../constants.ts';
import { canManageSmartInbox } from '../../../../users/utils.ts';

const UserFilter = ({ filters, onFiltersChange, activeChannel, channels, filterName }) => {
    const { t } = useTranslation('assistance');

    // TODO: maybe move activeChannel to context
    const { user } = useApplicationContext();

    // the smart inbox list needs the smart inbox permission; others only get the plain user filter
    const { data: smartInboxData } = useQuery(GET_SMART_INBOX_INTEGRATIONS, {
        skip: filterName !== UserFilterName.CREATED_BY || !canManageSmartInbox(user),
        fetchPolicy: 'cache-first',
    });

    const createdSourceFilter = filters.find((f: IFilter) => f.name === UserFilterName.CREATED_SOURCE);
    const isSmartInboxOnly =
        createdSourceFilter?.value === 'SMART_INBOX' ||
        (Array.isArray(createdSourceFilter?.value) && createdSourceFilter?.value.includes('SMART_INBOX'));

    const filter = filters.find((f: IFilter) => f.name === filterName);

    const actualSelected = filter ? (Array.isArray(filter.value) ? filter.value : [filter.value]) : [];

    const VIRTUAL_SMART_INBOX_VALUE = '__smart_inbox_toggle__';
    const selected =
        filterName === UserFilterName.CREATED_BY && isSmartInboxOnly && actualSelected.length === 0
            ? [VIRTUAL_SMART_INBOX_VALUE]
            : actualSelected;

    const handleSelectedChange = (selected: string[]) => {
        const realSelected = selected.filter((val: any) => val !== VIRTUAL_SMART_INBOX_VALUE);

        let newFilters = filters.filter((f: any) => f.name !== filterName);
        if (realSelected.length) {
            newFilters.push({ name: filterName, value: realSelected, exclude: false, expression: 'eq' });
        } else if (filterName === UserFilterName.CREATED_BY) {
            newFilters = newFilters.filter((f: any) => f.name !== UserFilterName.CREATED_SOURCE);
        }
        onFiltersChange(newFilters);
    };

    // If there is no active channel, search in all of them
    const channelsToFilter = activeChannel ? [activeChannel] : channels;

    // Sort users by first name, but put the current user on top
    const allChannelUsers: any[] = channelsToFilter.flatMap((channel) =>
        channel.team?.users ? channel.team.users.filter((u) => u.email !== user.email) : []
    );
    const uniqueChannelUsers = Array.from(new Map(allChannelUsers.map((item) => [item.email, item])).values());

    uniqueChannelUsers.sort((a, b) => a.firstName.localeCompare(b.firstName));
    uniqueChannelUsers.unshift(user);

    const userOptions = uniqueChannelUsers.map((user) => ({
        value: user.email,
        label: `${user.firstName} ${user.lastName}`,
    }));

    const AUTOMATED_OPTION = { label: t('overview.archivedByAutomated'), value: 'automated' };

    const formatLabel = ({ defaultLabel, allLabel, options, selected, searchValue, multiple }) => {
        let allFilterOptions = filterName === UserFilterName.ASSISTED_BY ? [...options, AUTOMATED_OPTION] : options;

        if (filterName === UserFilterName.CREATED_BY && isSmartInboxOnly) {
            if (actualSelected.length === 1) {
                const selectedOption = userOptions.find((opt) => opt.value === actualSelected[0]);
                const name = selectedOption?.label || actualSelected[0];
                return `${defaultLabel} ${name} (${t('overview.filters.smartInbox')})`;
            } else if (actualSelected.length > 1) {
                return `${defaultLabel} (${actualSelected.length}) ${t('overview.filters.smartInbox')}`;
            }
            return `${defaultLabel} ${t('overview.filters.smartInbox')}`;
        }

        return defaultFormatLabel({
            defaultLabel,
            allLabel,
            options: allFilterOptions,
            selected,
            searchValue,
            multiple,
        });
    };

    const typeDisplay = camelCase(filterName);

    const handleSmartInboxToggle = (checked: boolean) => {
        const newFilters = filters.filter((f: any) => f.name !== UserFilterName.CREATED_SOURCE);
        if (checked) {
            newFilters.push({
                name: UserFilterName.CREATED_SOURCE,
                value: ['SMART_INBOX', 'SMART_INBOX_MANUAL_CREATION'],
                exclude: false,
                expression: 'eq',
            });
        }
        onFiltersChange(newFilters);
    };

    const extraContent =
        filterName == UserFilterName.ASSISTED_BY ? (
            <div className="sticky bottom-0 w-full pt-1 border-t border-solid border-line-strong bg-surface-weak">
                <DropdownMenu.CheckboxItem
                    onCheckedChange={(checked) => {
                        if (checked) {
                            handleSelectedChange([...selected, AUTOMATED_OPTION.value]);
                        } else {
                            handleSelectedChange(selected.filter((value) => value !== AUTOMATED_OPTION.value));
                        }
                    }}
                    checked={selected.includes(AUTOMATED_OPTION.value)}
                    onSelect={(e) => e.preventDefault()}
                >
                    <span>{AUTOMATED_OPTION.label}</span>
                </DropdownMenu.CheckboxItem>
            </div>
        ) : filterName == UserFilterName.CREATED_BY ? (
            <div className="sticky bottom-0 w-full pt-1 border-t border-solid border-line-strong bg-surface-weak">
                <SwitchRow
                    className="px-2 py-1.5 rounded"
                    label={t('overview.filters.smartInboxOnly')}
                    value={isSmartInboxOnly}
                    onValueChange={handleSmartInboxToggle}
                />
            </div>
        ) : null;

    return (
        <Filter
            allLabel={t(`overview.filters.${typeDisplay}.all`)}
            label={t(`overview.filters.${typeDisplay}.label`)}
            options={userOptions}
            selected={selected}
            onSelectedChange={handleSelectedChange}
            multiple
            showSearch={true}
            extraContent={extraContent}
            formatLabel={formatLabel}
            showMultipleCount={!(filterName === UserFilterName.CREATED_BY && isSmartInboxOnly)}
        />
    );
};

export default UserFilter;
